import { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { message } from 'antd'
import { Button, TextInput } from '@forwardflow/ui-v2'
import { CheckCircle02Icon, VolumeMaxIcon } from '@forwardflow/icons'
import VoiceAvatar1 from '@forwardflow/assets/onboarding/voice1.png'
import VoiceAvatar2 from '@forwardflow/assets/onboarding/voice2.png'
import { campaignV2Api } from '../../services'
import { useLiveCampaign, useVoices } from '../../queries/campaign-v2.queries'
import { useCampaignV2Store } from '../../store/editor.store'
import { IAssistant, IVoice } from '../../interfaces/campaign-v2.types'
import {
   EmptyNote,
   Initials,
   IStepFooter,
   ListCard,
   ListRow,
   Loading,
   StepFrame,
   TextButton,
} from '../shared'

const AVATARS = [VoiceAvatar1, VoiceAvatar2]

/**
 * Step 4 — the voice leads hear. The live roster is edited directly
 * (POST/PATCH/DELETE ai-assistant), separate from the draft. Repeated
 * assistant records are grouped by voice for display; their IDs stay intact.
 */
export const VoiceStep = ({ footer }: { footer: IStepFooter }) => {
   const { campaignId } = useCampaignV2Store()
   const { data: voices, isLoading: voicesLoading } = useVoices()
   const { data: live, mutate } = useLiveCampaign(campaignId)
   const roster = live?.aiAssistants || []
   const [busy, setBusy] = useState<string | null>(null)
   const [name, setName] = useState('')
   const [pick, setPick] = useState<string | null>(null)
   const [showAll, setShowAll] = useState(false)
   const [pickerOpen, setPickerOpen] = useState(false)
   const grouped = roster.reduce((groups, assistant) => {
      const list = groups.get(assistant.voiceId) || []
      groups.set(assistant.voiceId, [...list, assistant])
      return groups
   }, new Map<string, IAssistant[]>())

   const selected = voices?.find((v) => v.voiceId === pick) || voices?.[0]
   useEffect(() => {
      if (!name && selected) setName(selected.name)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selected?.voiceId])

   /* Preview playback, one at a time. */
   const [playingId, setPlayingId] = useState<string | null>(null)
   const audioRef = useRef<HTMLAudioElement | null>(null)
   const stop = () => {
      audioRef.current?.pause()
      audioRef.current = null
      setPlayingId(null)
   }
   useEffect(() => stop, [])
   const play = (v: IVoice | IAssistant) => {
      const id = 'voiceId' in v ? v.voiceId : ''
      if (playingId === id) return stop()
      stop()
      if (!v.previewUrl) return
      const audio = new Audio(v.previewUrl)
      audioRef.current = audio
      setPlayingId(id)
      audio.onended = () => setPlayingId(null)
      audio.onerror = () => setPlayingId(null)
      void audio.play().catch(() => setPlayingId(null))
   }

   const add = async () => {
      if (!campaignId || !selected) return
      setBusy('add')
      try {
         await campaignV2Api().addAssistant(campaignId, {
            assistantName: name.trim() || selected.name,
            voiceId: selected.voiceId,
            description: `Hello, I am ${name.trim() || selected.name}, your assistant`,
            avatar: selected.avatar || null,
            previewUrl: selected.previewUrl || null,
            gender: selected.gender || null,
         })
         await mutate()
         setPickerOpen(false)
         message.success(`${name.trim() || selected.name} added`)
      } catch (e) {
         message.error((e as Error).message || 'Could not add the voice')
      } finally {
         setBusy(null)
      }
   }

   const remove = async (a: IAssistant) => {
      if (!campaignId) return
      if (roster.length === 1 && live?.status === 'active') {
         message.warning(
            'A live campaign needs at least one voice. Add another first.'
         )
         return
      }
      setBusy(String(a.id))
      try {
         await campaignV2Api().removeAssistant(campaignId, a.id)
         await mutate()
      } catch (e) {
         message.error((e as Error).message || 'Could not remove the voice')
      } finally {
         setBusy(null)
      }
   }

   const rename = async (a: IAssistant, next: string) => {
      if (!campaignId || !next.trim() || next === a.assistantName) return
      try {
         await campaignV2Api().updateAssistant(campaignId, a.id, {
            assistantName: next.trim(),
         })
         await mutate()
      } catch (e) {
         message.error((e as Error).message || 'Could not rename')
      }
   }

   const shown = showAll ? voices || [] : (voices || []).slice(0, 2)
   const alreadyAdded = roster.some((a) => a.voiceId === selected?.voiceId)

   return (
      <StepFrame
         eyebrow="Step 4 of 8 · Voice"
         title="This is the voice your leads will hear"
         lede="Choose the voice your assistant uses for this campaign."
         footer={{
            ...footer,
            disabled: footer.disabled || !roster.length,
            action: roster.length ? footer.action : 'Add a voice to continue',
         }}
         testId="step-voice"
      >
         <ListCard
            title={
               roster.length
                  ? `${grouped.size} voice${grouped.size === 1 ? '' : 's'} on this campaign`
                  : 'No voice yet'
            }
            right={
               <TextButton
                  onClick={() => setPickerOpen(!pickerOpen)}
                  testId="open-voice-picker"
               >
                  {pickerOpen ? 'Close library' : 'Add voice'}
               </TextButton>
            }
            testId="roster"
         >
            {!roster.length && (
               <EmptyNote testId="roster-empty">
                  Use Add voice to preview and choose a voice.
               </EmptyNote>
            )}
            {[...grouped].map(([voiceId, assistants]) =>
               assistants.length === 1 ? (
                  <RosterRow
                     key={voiceId}
                     a={assistants[0]}
                     busy={busy === String(assistants[0].id)}
                     onRemove={() => remove(assistants[0])}
                     onRename={(n) => rename(assistants[0], n)}
                     playing={playingId === voiceId}
                     onPlay={() => play(assistants[0])}
                  />
               ) : (
                  <div key={voiceId} data-testid={`voice-group-${voiceId}`}>
                     <ListRow
                        icon={
                           <Initials
                              name={assistants[0].assistantName}
                              size={30}
                           />
                        }
                        title={[
                           ...new Set(assistants.map((a) => a.assistantName)),
                        ].join(', ')}
                        sub={`Voice ${voiceId}`}
                        right={
                           assistants[0].previewUrl ? (
                              <TextButton onClick={() => play(assistants[0])}>
                                 {playingId === voiceId ? 'Stop' : 'Play'}
                              </TextButton>
                           ) : undefined
                        }
                     />
                     <details className="px-4 pb-3 text-xs text-white/50">
                        <summary className="cursor-pointer">
                           Manage existing assistants ({assistants.length})
                        </summary>
                        <p className="py-3">
                           These saved assistants use the same voice.
                        </p>
                        {assistants.map((a) => (
                           <RosterRow
                              key={String(a.id)}
                              a={a}
                              busy={busy === String(a.id)}
                              onRemove={() => remove(a)}
                              onRename={(n) => rename(a, n)}
                              playing={playingId === a.voiceId}
                              onPlay={() => play(a)}
                           />
                        ))}
                     </details>
                  </div>
               )
            )}
         </ListCard>

         {pickerOpen && (
            <>
               <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
                  role="radiogroup"
                  data-testid="voices"
               >
                  {voicesLoading && !voices?.length && (
                     <Loading label="Loading voices" />
                  )}
                  {shown.map((voice, index) => {
                     const isSelected = selected?.voiceId === voice.voiceId
                     const isPlaying = playingId === voice.voiceId
                     return (
                        <button
                           key={voice.voiceId}
                           type="button"
                           role="radio"
                           aria-checked={isSelected}
                           data-testid={`voice-${index}`}
                           onClick={() => {
                              setPick(voice.voiceId)
                              if (!name || name === selected?.name)
                                 setName(voice.name)
                           }}
                           className={cn(
                              'rounded-xl border text-left p-3.5 flex items-center gap-3 transition-all',
                              isSelected
                                 ? 'border-[#9F7BFF] bg-[#9F7BFF]/10'
                                 : 'border-white/10 bg-white/[.02] hover:border-white/25'
                           )}
                        >
                           <div className="relative shrink-0">
                              <img
                                 src={
                                    voice.avatar ||
                                    AVATARS[index % AVATARS.length]
                                 }
                                 alt={voice.name}
                                 className={cn(
                                    'w-12 h-12 rounded-full object-cover relative z-10 transition-transform',
                                    isPlaying && 'scale-105'
                                 )}
                                 onError={(e) => {
                                    e.currentTarget.src =
                                       AVATARS[index % AVATARS.length]
                                 }}
                              />
                              {isPlaying && (
                                 <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-xl opacity-60 animate-pulse pointer-events-none" />
                              )}
                           </div>
                           <div className="flex-1 min-w-0">
                              <p className="text-white font-semibold text-sm flex items-center gap-2">
                                 {voice.name}
                              </p>
                              {voice.description && (
                                 <p className="text-[#99A1AF] text-xs mt-0.5 truncate">
                                    {voice.description}
                                 </p>
                              )}
                              <div className="flex items-center gap-3 mt-2">
                                 <span
                                    role="button"
                                    tabIndex={0}
                                    className={cn(
                                       'rounded-lg inline-flex items-center gap-1.5 border px-2.5 py-1 text-xs text-white cursor-pointer hover:bg-white/[.08]',
                                       isPlaying
                                          ? 'border-[#9F7BFF] bg-[#9F7BFF]/20'
                                          : 'border-white/15 bg-white/[.02]',
                                       !voice.previewUrl &&
                                          'opacity-40 cursor-not-allowed'
                                    )}
                                    title={
                                       voice.previewUrl
                                          ? undefined
                                          : 'No preview for this voice'
                                    }
                                    onClick={(e) => {
                                       e.stopPropagation()
                                       play(voice)
                                    }}
                                    onKeyDown={(e) => {
                                       if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault()
                                          play(voice)
                                       }
                                    }}
                                 >
                                    <VolumeMaxIcon width={14} />{' '}
                                    {isPlaying ? 'Stop' : 'Play'}
                                 </span>
                                 {isSelected && (
                                    <span className="flex items-center gap-1 text-xs font-medium text-[#A684FF]">
                                       <CheckCircle02Icon
                                          width={14}
                                          height={14}
                                       />{' '}
                                       Selected
                                    </span>
                                 )}
                              </div>
                           </div>
                        </button>
                     )
                  })}
               </div>
               {(voices?.length || 0) > 2 && (
                  <TextButton
                     onClick={() => setShowAll((v) => !v)}
                     testId="more-voices"
                  >
                     {showAll
                        ? 'Fewer voices'
                        : `More voices (${(voices?.length || 0) - 2})`}
                  </TextButton>
               )}

               <div className="flex items-end gap-3">
                  <TextInput
                     size="sm"
                     className="flex-1"
                     label="The name it introduces itself with"
                     description="“Hi, this is Dakotah from Patel Realty.”"
                     value={name}
                     onChange={(e) => setName(e.currentTarget.value)}
                     maxLength={40}
                     data-testid="assistant-name"
                  />
                  <Button
                     size="middle"
                     className="!h-9 mb-[22px]"
                     onClick={() => void add()}
                     loading={busy === 'add'}
                     disabled={
                        !selected || !name.trim() || !campaignId || alreadyAdded
                     }
                     data-testid="add-voice"
                  >
                     {alreadyAdded ? 'Already added' : 'Add this voice'}
                  </Button>
               </div>
            </>
         )}
      </StepFrame>
   )
}

const RosterRow = ({
   a,
   busy,
   onRemove,
   onRename,
   playing,
   onPlay,
}: {
   a: IAssistant
   busy: boolean
   onRemove: () => void
   onRename: (n: string) => void
   playing: boolean
   onPlay: () => void
}) => {
   const [editing, setEditing] = useState(false)
   const [value, setValue] = useState(a.assistantName)
   return (
      <ListRow
         icon={
            a.avatar ? (
               <img
                  src={a.avatar}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover"
               />
            ) : (
               <Initials name={a.assistantName} size={30} />
            )
         }
         title={
            editing ? (
               <TextInput
                  size="xs"
                  value={value}
                  onChange={(e) => setValue(e.currentTarget.value)}
                  onBlur={() => {
                     setEditing(false)
                     onRename(value)
                  }}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter')
                        (e.currentTarget as HTMLInputElement).blur()
                  }}
                  autoFocus
                  data-testid="rename-input"
               />
            ) : (
               a.assistantName
            )
         }
         sub={`Voice ${a.voiceId}`}
         right={
            <>
               {a.previewUrl && (
                  <TextButton onClick={onPlay} tone="muted">
                     {playing ? 'Stop' : 'Play'}
                  </TextButton>
               )}
               <TextButton onClick={() => setEditing(true)} tone="muted">
                  Rename
               </TextButton>
               <TextButton
                  onClick={onRemove}
                  tone="danger"
                  disabled={busy}
                  testId={`remove-${a.id}`}
               >
                  Remove
               </TextButton>
            </>
         }
         testId={`roster-${a.id}`}
      />
   )
}
